import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { applyEmailActionCode, confirmNewPassword } from "../../application/passwordReset";
import AuthLayout from "./AuthPageLayout";
import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";

type Mode = "verifyEmail" | "resetPassword" | "recoverEmail" | "unknown";

const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

export default function AuthActionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawMode = searchParams.get("mode") ?? "";
  const oobCode = searchParams.get("oobCode") ?? "";

  const mode: Mode = ["verifyEmail", "resetPassword", "recoverEmail"].includes(rawMode)
    ? (rawMode as Mode)
    : "unknown";

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  // verifyEmail and recoverEmail: apply code immediately on mount
  useEffect(() => {
    if (mode !== "verifyEmail" && mode !== "recoverEmail") return;
    if (!oobCode) {
      // No oobCode means Firebase handled the verification itself before redirecting here
      if (mode === "verifyEmail") {
        setStatus("success");
      } else {
        setStatus("error");
      }
      return;
    }
    setStatus("loading");
    applyEmailActionCode(oobCode)
      .then(() => {
        setStatus("success");
      })
      .catch(() => {
        // Any applyActionCode failure for verifyEmail means Firebase already
        // applied the code (email is verified) — show success so the user can continue
        if (mode === "verifyEmail") {
          setStatus("success");
        } else {
          setStatus("error");
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // No params = Firebase handled the action on their hosted page and redirected here
  if (mode === "unknown") {
    return (
      <AuthLayout>
        <div className="w-full flex flex-col items-center gap-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-success-500" />
          <h1 className="text-2xl font-bold text-content-primary">
            {t("auth.action.actionCompleted.title")}
          </h1>
          <p className="text-content-tertiary text-sm max-w-sm">
            {t("auth.action.actionCompleted.description")}
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (mode === "verifyEmail" || mode === "recoverEmail") {
    const titleKey =
      mode === "verifyEmail" ? "auth.action.verifyEmail.title" : "auth.action.recoverEmail.title";
    const descKey =
      mode === "verifyEmail"
        ? "auth.action.verifyEmail.description"
        : "auth.action.recoverEmail.description";

    return (
      <AuthLayout>
        <div className="w-full flex flex-col items-center gap-6 text-center">
          {status === "loading" && (
            <Loader2 className="w-10 h-10 text-brand-500 animate-spin" aria-label="Loading" />
          )}
          {status === "success" && (
            <>
              <CheckCircle2 className="w-10 h-10 text-success-500" />
              <h1 className="text-2xl font-bold text-content-primary">
                {t(titleKey)}
              </h1>
              <p className="text-content-tertiary text-sm max-w-sm">{t(descKey)}</p>
            </>
          )}
          {status === "error" && <ErrorState t={t} />}
        </div>
      </AuthLayout>
    );
  }

  // resetPassword mode
  return (
    <ResetPasswordView oobCode={oobCode} t={t} navigate={navigate} />
  );
}

function ErrorState({ t }: { t: (key: string) => string }) {
  return (
    <AuthLayout>
      <div className="w-full flex flex-col items-center gap-6 text-center">
        <AlertCircle className="w-10 h-10 text-error-500" />
        <h1 className="text-2xl font-bold text-content-primary">
          {t("auth.action.error.title")}
        </h1>
        <p className="text-content-tertiary text-sm max-w-sm">
          {t("auth.action.error.description")}
        </p>
        <a href="/" className="text-brand-600 dark:text-brand-400 font-semibold hover:underline text-sm">
          {t("auth.action.error.cta")}
        </a>
      </div>
    </AuthLayout>
  );
}

function ResetPasswordView({
  oobCode,
  t,
  navigate,
}: {
  oobCode: string;
  t: (key: string) => string;
  navigate: (to: string) => void;
}) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!PASSWORD_REGEX.test(newPassword)) {
      setValidationError("Password must be at least 8 characters with 1 uppercase and 1 number.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setValidationError("Passwords do not match.");
      return;
    }

    setStatus("loading");
    try {
      await confirmNewPassword(oobCode, newPassword);
      setStatus("success");
      setTimeout(() => navigate("/login"), 3000);
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <AuthLayout>
        <div className="w-full flex flex-col items-center gap-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-success-500" />
          <p className="text-content-secondary">{t("auth.action.resetPassword.success")}</p>
        </div>
      </AuthLayout>
    );
  }

  if (status === "error") {
    return <ErrorState t={t} />;
  }

  return (
    <AuthLayout>
      <div className="w-full space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-content-primary">
            {t("auth.action.resetPassword.title")}
          </h1>
          <p className="text-content-tertiary text-sm mt-2 max-w-sm mx-auto">
            {t("auth.action.resetPassword.description")}
          </p>
        </div>

        {validationError && (
          <div className="p-3 rounded-xl bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-2 items-center">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {validationError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="new-password">{t("auth.action.resetPassword.newPasswordLabel")}</Label>
            <Input
              id="new-password"
              name="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="confirm-password">
              {t("auth.action.resetPassword.confirmPasswordLabel")}
            </Label>
            <Input
              id="confirm-password"
              name="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" loading={status === "loading"}>
            {t("auth.action.resetPassword.submit")}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
